import Scrollbars from "react-custom-scrollbars-2";
import { useContext, useMemo } from "react";
import { useReconciliationSummaryQuery } from "../../api/reconciliation.api";
import { FinancialAccount } from "../../common/types/financialAccount";
import {
    fromIntToMonetary,
    fromMonetaryToInt,
} from "../../common/helpers/monetary";
import { TransactionForReconciliation } from "../../common/contracts/reconciliation.contract";
import { ReconciliationFooter } from "./ReconciliationFooter";
import { ReconciliationTransactionsTable } from "./ReconciliationTransactionsTable";
import styles from "./Reconciliation.module.scss";
import { ReconciliationContext } from "./Reconciliation.context";

interface Props {
    financialAccount: FinancialAccount;
    onBack: () => void;
    onHide: () => void;
}

export const ReconciliationTransactionsReview: React.FC<Props> = ({
    onBack,
    onHide,
    financialAccount,
}) => {
    const { data: summary } = useReconciliationSummaryQuery(
        financialAccount.id,
    );
    const { financialTransactionsToRemoveIds, statementTransactionsToAddIds } =
        useContext(ReconciliationContext);
    const pairs = summary?.transactionsPairs ?? [];
    let changeInBalance = summary
        ? fromMonetaryToInt(summary.statementsClosingBalance) -
          fromMonetaryToInt(summary.statementsOpeningBalance)
        : 0;
    let { cashIn, cashOut } = useMemo(
        () =>
            summary?.transactionsPairs.reduce(
                (acc, pair) => {
                    let transaction: TransactionForReconciliation | undefined =
                        pair.fromAccount;
                    if (
                        pair.fromAccount &&
                        financialTransactionsToRemoveIds.includes(
                            pair.fromAccount.financialTransactionId,
                        )
                    ) {
                        transaction = undefined;
                    }
                    if (
                        pair.fromStatement &&
                        statementTransactionsToAddIds.includes(
                            pair.fromStatement.statementTransactionId,
                        )
                    ) {
                        transaction = pair.fromStatement;
                    }

                    if (transaction && transaction.amount > 0) {
                        acc.cashIn += fromMonetaryToInt(transaction.amount);
                    } else if (transaction && transaction.amount < 0) {
                        acc.cashOut += Math.abs(
                            fromMonetaryToInt(transaction.amount),
                        );
                    }

                    return acc;
                },
                { cashIn: 0, cashOut: 0 },
            ) ?? { cashIn: 0, cashOut: 0 },
        [
            summary?.transactionsPairs,
            financialTransactionsToRemoveIds,
            statementTransactionsToAddIds,
        ],
    );

    let net = cashIn - cashOut;

    const difference = fromIntToMonetary(Math.abs(changeInBalance - net));
    net = fromIntToMonetary(net);
    cashIn = fromIntToMonetary(cashIn);
    cashOut = fromIntToMonetary(cashOut);
    changeInBalance = fromIntToMonetary(changeInBalance);

    return (
        <div className={styles.container}>
            <Scrollbars>
                <ReconciliationTransactionsTable
                    pairs={pairs}
                    openingBalance={summary?.statementsOpeningBalance ?? 0}
                    closingBalance={summary?.statementsClosingBalance ?? 0}
                    changeInBalance={changeInBalance}
                    cashIn={cashIn}
                    cashOut={cashOut}
                    net={net}
                    difference={difference}
                />
            </Scrollbars>
            {summary && (
                <ReconciliationFooter
                    onHide={onHide}
                    onBack={onBack}
                    pairs={pairs}
                    financialAccount={financialAccount}
                    reconciles={difference === 0}
                />
            )}
        </div>
    );
};
